import type { CatchballParticipantRef } from '@/types/prioritization/catchballVirtualConfig';
import type { DesignerPath, OrgPathNode } from '../types';

export type PathNodeAction = 'add' | 'remove';

export interface CanAddToPathResult {
  can: boolean;
  action?: PathNodeAction;
  reason: string;
}

export function buildOrgById(nodes: OrgPathNode[]): Map<string, OrgPathNode> {
  return new Map(nodes.map((n) => [n.id, n]));
}

export function isDescendantOrSelf(
  nodeId: string,
  ancestorId: string,
  orgById: Map<string, OrgPathNode>,
): boolean {
  if (nodeId === ancestorId) return true;
  const node = orgById.get(nodeId);
  if (!node?.parentId) return false;
  return isDescendantOrSelf(node.parentId, ancestorId, orgById);
}

export function canAddToPath(
  nodeId: string,
  activePath: DesignerPath | undefined,
  orgById: Map<string, OrgPathNode>,
): CanAddToPathResult {
  const node = orgById.get(nodeId);
  if (!node) return { can: false, reason: '' };
  if (node.relativeLevel === 0) {
    return { can: false, reason: 'Anchor node — always included' };
  }
  if (!activePath) return { can: false, reason: 'No active path' };

  if (activePath.nodes.includes(nodeId)) {
    return { can: true, action: 'remove', reason: 'Click to remove from path' };
  }

  if (!node.parentId || !activePath.nodes.includes(node.parentId)) {
    const parentNode = node.parentId ? orgById.get(node.parentId) : undefined;
    return {
      can: false,
      reason: `Add ${parentNode?.name ?? 'parent'} to path first`,
    };
  }

  // A second stop in the same row is a FORK, not a replacement (owner ruling
  // 2026-10-13): one path may send its result to several stops below it.
  const siblingsOnPath = activePath.nodes.filter(
    (nid) => nid !== nodeId && orgById.get(nid)?.parentId === node.parentId,
  );
  if (siblingsOnPath.length > 0) {
    const parentNode = orgById.get(node.parentId);
    return {
      can: true,
      action: 'add',
      reason: `Add to path — forks below ${parentNode?.name ?? 'the stop above'}`,
    };
  }

  return { can: true, action: 'add', reason: 'Add to path' };
}

/** Drop node assignments for ids no longer on the path — a cleared node's picked user/group must not linger invisibly. */
function pruneNodeAssignments(path: DesignerPath, nextNodes: string[]): DesignerPath['nodeAssignments'] {
  if (!path.nodeAssignments) return path.nodeAssignments;
  const keep = new Set(nextNodes);
  const next: DesignerPath['nodeAssignments'] = {};
  for (const [nodeId, ref] of Object.entries(path.nodeAssignments)) {
    if (keep.has(nodeId)) next[nodeId] = ref;
  }
  return next;
}

export function toggleNodeOnPath(
  nodeId: string,
  path: DesignerPath,
  orgById: Map<string, OrgPathNode>,
): DesignerPath {
  const { can, action } = canAddToPath(nodeId, path, orgById);
  if (!can || !action) return path;

  const node = orgById.get(nodeId);
  if (!node) return path;

  if (action === 'remove') {
    const nextNodes = path.nodes.filter((nid) => {
      const n = orgById.get(nid);
      if (!n) return false;
      return (
        n.relativeLevel < node.relativeLevel ||
        nid === node.parentId ||
        !isDescendantOrSelf(nid, nodeId, orgById)
      );
    });
    return { ...path, nodes: nextNodes, nodeAssignments: pruneNodeAssignments(path, nextNodes) };
  }

  return { ...path, nodes: [...path.nodes, nodeId] };
}

/**
 * The stop a path reaches `nodeId` from. A path is a tree down the org: each
 * stop hangs off its org parent when that parent is on the path. Stops with no
 * such parent (custom paths name their own order) hang off the stop before
 * them in the list, which keeps a plain chain a plain chain.
 */
export function pathParentOf(
  path: Pick<DesignerPath, 'nodes'>,
  nodeId: string,
  orgById: Map<string, Pick<OrgPathNode, 'parentId'>>,
): string | null {
  const index = path.nodes.indexOf(nodeId);
  if (index <= 0) return null;
  const orgParent = orgById.get(nodeId)?.parentId;
  if (orgParent && path.nodes.includes(orgParent)) return orgParent;
  return path.nodes[index - 1];
}

/** Stops on the path directly below `nodeId`. More than one is a fork. */
export function pathChildrenOf(
  path: Pick<DesignerPath, 'nodes'>,
  nodeId: string,
  orgById: Map<string, Pick<OrgPathNode, 'parentId'>>,
): string[] {
  return path.nodes.filter((nid) => pathParentOf(path, nid, orgById) === nodeId);
}

/** How many stops sit between the top of the path and `nodeId` (the top is 0). */
export function pathDepthOf(
  path: Pick<DesignerPath, 'nodes'>,
  nodeId: string,
  orgById: Map<string, Pick<OrgPathNode, 'parentId'>>,
): number {
  let depth = 0;
  let cursor = pathParentOf(path, nodeId, orgById);
  while (cursor && depth <= path.nodes.length) {
    depth += 1;
    cursor = pathParentOf(path, cursor, orgById);
  }
  return depth;
}

/** True when any stop on the path sends to more than one stop below it. */
export function isForkedPath(
  path: Pick<DesignerPath, 'nodes'>,
  orgById: Map<string, Pick<OrgPathNode, 'parentId'>>,
): boolean {
  return path.nodes.some((nid) => pathChildrenOf(path, nid, orgById).length > 1);
}

/**
 * The path in words: stops in the same row joined with "+", rows joined with
 * "→". "Company → Product → Billing + Platform" is a path that forks below
 * Product.
 */
export function describePath(
  path: Pick<DesignerPath, 'nodes'>,
  orgById: Map<string, Pick<OrgPathNode, 'parentId' | 'name'>>,
): string {
  const rows: string[][] = [];
  for (const nid of path.nodes) {
    const name = orgById.get(nid)?.name;
    if (!name) continue;
    const depth = pathDepthOf(path, nid, orgById);
    (rows[depth] ??= []).push(name);
  }
  return rows.filter(Boolean).map((row) => row.join(' + ')).join(' → ');
}

export function getPathsForNode(nodeId: string, paths: DesignerPath[]): DesignerPath[] {
  return paths.filter((p) => p.nodes.includes(nodeId));
}

/** Assignees named on this org node across every path — not just the active one. */
export function assignmentsForNode(
  nodeId: string,
  paths: DesignerPath[],
): Array<{ path: DesignerPath; ref: CatchballParticipantRef }> {
  const out: Array<{ path: DesignerPath; ref: CatchballParticipantRef }> = [];
  for (const path of paths) {
    if (!path.nodes.includes(nodeId)) continue;
    const ref = path.nodeAssignments?.[nodeId];
    if (ref) out.push({ path, ref });
  }
  return out;
}

/**
 * When a node joins a path that has no pick yet, copy the person already named
 * there on another path so the name stays on the node and the same person can
 * ride more than one ball.
 */
export function copyAssignmentFromPaths(
  path: DesignerPath,
  nodeId: string,
  paths: DesignerPath[],
): DesignerPath {
  if (path.nodeAssignments?.[nodeId]) return path;
  const donor = paths.find((p) => p.id !== path.id && p.nodeAssignments?.[nodeId]);
  const ref = donor?.nodeAssignments?.[nodeId];
  if (!ref) return path;
  return { ...path, nodeAssignments: { ...(path.nodeAssignments ?? {}), [nodeId]: ref } };
}

export function parentInAnyPath(nodeId: string, paths: DesignerPath[], orgById: Map<string, OrgPathNode>): boolean {
  const node = orgById.get(nodeId);
  if (!node?.parentId) return true;
  return paths.some((p) => p.nodes.includes(node.parentId!));
}

/** Every named person/group in the design — offered again so they can ride a second path. */
export function assigneesOnDesign(paths: DesignerPath[]): CatchballParticipantRef[] {
  const seen = new Set<string>();
  const out: CatchballParticipantRef[] = [];
  for (const path of paths) {
    for (const ref of Object.values(path.nodeAssignments ?? {})) {
      const key = `${ref.type}:${ref.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(ref);
    }
  }
  return out;
}

/**
 * The owner named on a unit, on whichever path names one. An owner belongs to
 * the unit for the whole cycle, not to one path: the canvas showed only the
 * selected path's pick, so switching paths read as the owner disappearing.
 */
export function ownerOfUnit(nodeId: string, paths: DesignerPath[]): CatchballParticipantRef | null {
  for (const path of paths) {
    const ref = path.nodeAssignments?.[nodeId];
    if (ref && path.nodes.includes(nodeId)) return ref;
  }
  return null;
}

/**
 * Who answers for a unit that names no owner: the nearest unit above it that
 * does, and at the top the cycle's owner. `from` names where it came from
 * (null = the cycle).
 */
export function ownerAbove(
  nodeId: string,
  paths: DesignerPath[],
  orgById: Map<string, Pick<OrgPathNode, 'parentId'>>,
  cycleOwner: CatchballParticipantRef | null,
): { ref: CatchballParticipantRef; from: string | null } | null {
  let cursor = orgById.get(nodeId)?.parentId ?? null;
  let guard = 0;
  while (cursor && guard++ < 64) {
    const ref = ownerOfUnit(cursor, paths);
    if (ref) return { ref, from: cursor };
    cursor = orgById.get(cursor)?.parentId ?? null;
  }
  return cycleOwner ? { ref: cycleOwner, from: null } : null;
}

/** Name (or clear) a unit's owner on every path that runs through it. */
export function setOwnerOnUnit(
  paths: DesignerPath[],
  nodeId: string,
  ref: CatchballParticipantRef | null,
): DesignerPath[] {
  return paths.map((p) => {
    if (!p.nodes.includes(nodeId)) return p;
    const next = { ...(p.nodeAssignments ?? {}) };
    if (ref) next[nodeId] = ref;
    else delete next[nodeId];
    return { ...p, nodeAssignments: next };
  });
}

/**
 * The owners the launch sends: a stop with no owner of its own goes to the
 * owner above it (owner ruling 2026-11-03: "if there's no owner, it goes back
 * to the level above"). The anchor is the cycle owner's and is left alone.
 */
export function withInheritedOwners(
  path: DesignerPath,
  paths: DesignerPath[],
  orgById: Map<string, Pick<OrgPathNode, 'parentId' | 'relativeLevel'>>,
  cycleOwner: CatchballParticipantRef | null,
): DesignerPath {
  const filled = { ...(path.nodeAssignments ?? {}) };
  for (const nodeId of path.nodes) {
    if (filled[nodeId] || orgById.get(nodeId)?.relativeLevel === 0) continue;
    const above = ownerOfUnit(nodeId, paths) ?? ownerAbove(nodeId, paths, orgById, cycleOwner)?.ref ?? null;
    if (above) filled[nodeId] = above;
  }
  return { ...path, nodeAssignments: filled };
}
